import { postToken } from './postToken';
import { generateSessionId } from '../functions/functions';

/**
 * Gestor centralizado de tokens de autenticación con recuperación automática
 * 
 * @description Maneja la generación, almacenamiento y renovación automática de tokens
 * de autenticación para servicios protegidos. Implementa un sistema robusto de
 * reintentos con backoff exponencial, cola de peticiones y recuperación ante fallos.
 * Previene múltiples regeneraciones simultáneas y garantiza consistencia en el estado.
 * 
 * @class TokenManager
 * 
 * @example
 * // Uso básico - obtener token válido
 * const token = await tokenManager.getValidToken();
 * console.log('Token obtenido:', token);
 * 
 * @example
 * // Manejo de token expirado (401)
 * try {
 *   const result = await apiCall(data, token);
 *   if (result.statusCode === 401) {
 *     const newToken = await tokenManager.handleUnauthorized();
 *     const retryResult = await apiCall(data, newToken);
 *   }
 * } catch (error) {
 *   console.error('Token service unavailable:', error);
 * }
 * 
 * @example
 * // Múltiples peticiones concurrentes
 * const promises = [
 *   tokenManager.getValidToken(),
 *   tokenManager.getValidToken(),
 *   tokenManager.getValidToken()
 * ];
 * const tokens = await Promise.all(promises); // Solo una generación
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ./postToken.js} Servicio de generación de tokens
 * @see {@link ./apiWrapper.js} Wrapper que utiliza este manager
 * @see {@link ../functions/functions.js} Para generateSessionId()
 */
class TokenManager {
  /**
   * Inicializa el gestor de tokens con configuración por defecto
   * 
   * @constructor
   * @description Establece el estado inicial del manager con parámetros
   * optimizados para producción: máximo 3 reintentos, backoff de 1 segundo
   * y sistema de cola para peticiones concurrentes.
   */
  constructor() {
    /** @private {boolean} Indica si hay una regeneración en progreso */
    this.isRefreshing = false;
    
    /** @private {Array<{resolve: Function, reject: Function}>} Cola de peticiones pendientes */
    this.failedQueue = [];
    
    /** @private {number} Contador actual de reintentos */
    this.retryCount = 0;
    
    /** @private {number} Máximo número de reintentos permitidos */
    this.maxRetries = 3;
    
    /** @private {number} Delay inicial en milisegundos para backoff exponencial */
    this.backoffDelay = 1000; // 1 segundo inicial
  }

  /**
   * Obtiene un token válido, generando uno nuevo si es necesario
   * 
   * @async
   * @method getValidToken
   * 
   * @returns {Promise<string>} Token de autenticación válido
   * 
   * @throws {Error} Si el servicio de tokens no está disponible después de reintentos
   * 
   * @description Verifica si existe un token en localStorage. Si no existe,
   * genera uno nuevo automáticamente. Maneja la concurrencia evitando múltiples
   * generaciones simultáneas mediante el sistema de cola.
   * 
   * @example
   * // Obtener token para autenticación
   * try {
   *   const token = await tokenManager.getValidToken();
   *   const headers = { 'Authorization': `Bearer ${token}` };
   * } catch (error) {
   *   console.error('No se pudo obtener token:', error);
   * }
   */
  async getValidToken() {
    const token = localStorage.getItem('sessionToken');
    if (token) return token;
    
    return await this.generateNewToken();
  }

  /**
   * Genera un nuevo token de autenticación con reintentos automáticos
   * 
   * @async
   * @method generateNewToken
   * @private
   * 
   * @returns {Promise<string>} Nuevo token de autenticación
   * 
   * @throws {Error} Si falla después del máximo de reintentos
   * 
   * @description Implementa un sistema robusto de generación de tokens con:
   * - Cola de peticiones para evitar duplicados
   * - Reintentos automáticos con backoff exponencial
   * - Manejo de errores y limpieza de estado
   * - Resolución/rechazo de todas las peticiones en cola
   * 
   * Patrón de backoff: 1s, 2s, 4s para los 3 reintentos
   */
  async generateNewToken() {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const sessionId = this.getSessionId();
      const response = await postToken({ user_id: sessionId });

      if (response?.statusCode === 200 && response.data?.token) {
        const newToken = response.data.token;
        localStorage.setItem('sessionToken', newToken);
        
        // Reset retry count en éxito
        this.retryCount = 0;
        
        // Resolver todas las peticiones en cola
        this.failedQueue.forEach(({ resolve }) => resolve(newToken));
        this.failedQueue = [];
        
        return newToken;
      } else {
        throw new Error(`Token generation failed with status: ${response?.statusCode}`);
      }
    } catch (error) {
      console.error('Error generating token:', error);
      
      // Incrementar contador de reintentos
      this.retryCount++;
      
      // Si no hemos alcanzado el máximo de reintentos, intentar de nuevo
      if (this.retryCount < this.maxRetries) {
        console.log(`Reintentando generación de token (${this.retryCount}/${this.maxRetries})...`);
        
        // Esperar con backoff exponencial
        await this.delay(this.backoffDelay * Math.pow(2, this.retryCount - 1));
        
        this.isRefreshing = false;
        return await this.generateNewToken();
      }
      
      // Máximo de reintentos alcanzado
      console.error('Máximo de reintentos alcanzado para generación de token');
      
      // Rechazar todas las peticiones en cola
      this.failedQueue.forEach(({ reject }) => reject(error));
      this.failedQueue = [];
      
      throw new Error('Token service unavailable after multiple retries');
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Implementa delay asíncrono para backoff exponencial
   * 
   * @async
   * @method delay
   * @private
   * 
   * @param {number} ms - Milisegundos a esperar
   * 
   * @returns {Promise<void>} Promise que se resuelve después del delay
   * 
   * @description Utilidad para implementar esperas no bloqueantes
   * en el sistema de reintentos. Permite backoff exponencial
   * sin bloquear el hilo principal.
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Obtiene o genera un ID de sesión único persistente
   * 
   * @method getSessionId
   * @private
   * 
   * @returns {string} ID de sesión único
   * 
   * @description Maneja la persistencia del ID de sesión en localStorage.
   * Si no existe, genera uno nuevo usando la función utilitaria.
   * El ID se mantiene durante toda la sesión del navegador.
   * 
   * @see {@link ../functions/functions.js#generateSessionId} Generador de IDs
   */
  getSessionId() {
    let sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = generateSessionId();
      localStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  /**
   * Maneja errores de autorización (401) regenerando tokens automáticamente
   * 
   * @async
   * @method handleUnauthorized
   * 
   * @returns {Promise<string>} Nuevo token de autenticación
   * 
   * @throws {Error} Si no se puede regenerar el token después de reintentos
   * 
   * @description Método principal para recuperación automática ante tokens
   * expirados. Limpia el token actual, genera uno nuevo y maneja fallos
   * críticos limpiando completamente el estado de sesión.
   * 
   * Flujo de recuperación:
   * 1. Elimina token expirado del localStorage
   * 2. Intenta generar nuevo token con reintentos
   * 3. Si falla completamente, limpia toda la sesión
   * 4. Resetea contadores para futuros intentos
   * 
   * @example
   * // Uso típico en wrapper de API
   * if (response.statusCode === 401) {
   *   const newToken = await tokenManager.handleUnauthorized();
   *   return await apiCall(data, newToken);
   * }
   */
  async handleUnauthorized() {
    localStorage.removeItem('sessionToken');
    
    try {
      return await this.generateNewToken();
    } catch (error) {
      // Si falla la generación de token, limpiar todo
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('sessionId');
      
      // Reset retry count para futuros intentos
      this.retryCount = 0;
      
      throw error;
    }
  }
}

/**
 * Instancia singleton del gestor de tokens
 * 
 * @type {TokenManager}
 * @description Instancia única y compartida del TokenManager para toda la aplicación.
 * Garantiza consistencia en el manejo de tokens y evita conflictos entre
 * múltiples instancias. Debe ser importada y utilizada por todos los servicios
 * que requieran autenticación.
 * 
 * @example
 * // Importar y usar en servicios
 * import { tokenManager } from './tokenManager';
 * 
 * const token = await tokenManager.getValidToken();
 * const headers = { 'Authorization': `Bearer ${token}` };
 */
export const tokenManager = new TokenManager();